/**
 * Configuration management for Paean CLI
 * Stores authentication tokens and user preferences in ~/.paean/config.json
 */

import Conf from "conf";
import { homedir } from "os";
import { join } from "path";

export interface PaeanConfig {
  // Authentication
  token?: string;
  refreshToken?: string;
  userId?: number;
  email?: string;
  expiresAt?: string;

  // Device Session (for TDRC Worker)
  deviceSessionId?: string;

  // API Configuration
  apiUrl?: string;
  webUrl?: string;

  // User Preferences
  defaultPriority?: "high" | "medium" | "low";
  outputFormat?: "table" | "json" | "minimal";
}

const CONFIG_DEFAULTS: PaeanConfig = {
  apiUrl: "https://api.paean.ai",
  webUrl: "https://app.paean.ai",
  defaultPriority: "medium",
  outputFormat: "table",
};

// Store config in ~/.paean directory (or ~/.paean-dev when PAEAN_CONFIG_DIR is set)
// Lazily initialized so --dev flag can set PAEAN_CONFIG_DIR before first access
let _config: Conf<PaeanConfig> | null = null;
function getConfInstance(): Conf<PaeanConfig> {
  if (!_config) {
    const configDir = process.env.PAEAN_CONFIG_DIR || "paean";
    _config = new Conf<PaeanConfig>({
      projectName: configDir,
      cwd: join(homedir(), `.${configDir}`),
      defaults: CONFIG_DEFAULTS,
      schema: {
        token: { type: "string" },
        refreshToken: { type: "string" },
        userId: { type: "number" },
        email: { type: "string" },
        expiresAt: { type: "string" },
        apiUrl: { type: "string" },
        webUrl: { type: "string" },
        defaultPriority: { type: "string", enum: ["high", "medium", "low"] },
        outputFormat: { type: "string", enum: ["table", "json", "minimal"] },
      },
    });
  }
  return _config;
}

// Shorthand for lazy config access
const c = () => getConfInstance();

/**
 * Get the full configuration
 */
export function getConfig(): PaeanConfig {
  return c().store;
}

/**
 * Get a specific config value
 */
export function getConfigValue<K extends keyof PaeanConfig>(
  key: K
): PaeanConfig[K] {
  return c().get(key);
}

/**
 * Set a specific config value
 */
export function setConfigValue<K extends keyof PaeanConfig>(
  key: K,
  value: PaeanConfig[K]
): void {
  c().set(key, value);
}

/**
 * Set multiple config values at once
 */
export function setConfig(values: Partial<PaeanConfig>): void {
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) {
      c().set(key as keyof PaeanConfig, value);
    }
  }
}

/**
 * Check if user is authenticated
 * Environment variable PAEAN_TOKEN bypasses expiry checks
 */
export function isAuthenticated(): boolean {
  if (process.env.PAEAN_TOKEN) return true;

  const token = c().get("token");
  const expiresAt = c().get("expiresAt");

  if (!token) return false;

  if (expiresAt) {
    const expiry = new Date(expiresAt).getTime();
    const now = Date.now();
    const buffer = 5 * 60 * 1000;
    if (expiry - buffer < now) {
      return false;
    }
  }

  return true;
}

/**
 * Get the authentication token
 * Supports PAEAN_TOKEN environment variable override
 */
export function getToken(): string | undefined {
  return process.env.PAEAN_TOKEN || c().get("token");
}

/**
 * Get the API URL
 * Supports PAEAN_API_URL environment variable override
 */
export function getApiUrl(): string {
  return process.env.PAEAN_API_URL || c().get("apiUrl") || CONFIG_DEFAULTS.apiUrl!;
}

/**
 * Get the Web URL
 * Supports PAEAN_WEB_URL environment variable override
 */
export function getWebUrl(): string {
  return process.env.PAEAN_WEB_URL || c().get("webUrl") || CONFIG_DEFAULTS.webUrl!;
}

/**
 * Get the home config directory path (~/.paean or ~/.paean-dev)
 * Respects PAEAN_CONFIG_DIR environment variable.
 */
export function getConfigDir(): string {
  const configDir = process.env.PAEAN_CONFIG_DIR || "paean";
  return join(homedir(), `.${configDir}`);
}

/**
 * Check if debug mode is enabled via environment variable
 */
export function isDebugEnabled(): boolean {
  return process.env.PAEAN_DEBUG === '1' || process.env.PAEAN_DEBUG === 'true';
}

/**
 * Store authentication data
 */
export function storeAuth(data: {
  token: string;
  refreshToken?: string;
  userId?: number;
  email?: string;
  expiresAt?: string;
}): void {
  setConfig({
    token: data.token,
    refreshToken: data.refreshToken,
    userId: data.userId,
    email: data.email,
    expiresAt: data.expiresAt,
  });
}

/**
 * Get the refresh token
 */
export function getRefreshToken(): string | undefined {
  return c().get("refreshToken");
}

/**
 * Check if the current token is near expiry (within buffer window)
 * Returns true if the token exists but will expire soon
 */
export function isTokenNearExpiry(): boolean {
  const token = c().get("token");
  const expiresAt = c().get("expiresAt");

  if (!token || !expiresAt) return false;

  const expiry = new Date(expiresAt).getTime();
  if (isNaN(expiry)) return false;

  const now = Date.now();
  const buffer = 10 * 60 * 1000;
  return expiry - buffer < now && expiry > now;
}

/**
 * Clear authentication data (logout)
 */
export function clearAuth(): void {
  c().delete("token");
  c().delete("refreshToken");
  c().delete("userId");
  c().delete("email");
  c().delete("expiresAt");
}

/**
 * Get the config file path
 */
export function getConfigPath(): string {
  return c().path;
}

/**
 * Reset config to defaults
 */
export function resetConfig(): void {
  c().clear();
}
